import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import Header from "../menu/Header";
import Footer from "../footer/Footer";
import PageIntro from "../homepage/desktop_components/intro_section/IntroSection";

import { ContactFormsTexts } from "../../enums/page_texts/contact_forms_texts";
import { TopSectionsPictures } from "../../enums/images";
import StepFormModal from "./StepFormModal";
import EventTypeSelector from "../general/EventTypeSelector";
import DoubleEntryTable from "../formulas/DoubleEntryTable";
import RollingGallery from "../general/RollingGallery";
import {
  StyledBackgroundDiv,
  IntroTypography,
} from "../../styles/common.style";
import PublicEventsFormulasTable from "../formulas/PublicEventsFormulasTable";

import {
  fetchExtraCards,
  fetchFaqItems,
  loadContactFormsPageData,
} from "../../services/api";
import {
  FormulasPageGeneralTexts,
  PrivateEventsDescription,
  PublicEventsDescription,
  PublicEventsFormulasTableTexts,
} from "../../enums/page_texts/formulas_texts";
import LoadingSpinner from "../general/LoadingSpinner";
import ApiErrorScreen from "../general/ApiErrorScreen";
import StaticFooter from "../footer/StaticFooter";
import EventTypeDescriptionSection from "../formulas/EventTypeDescriptionSection";
import SeoHelmet from "../general/SeoHelmet";
import FaqSection from "../general/FaqSection";
import BreadcrumbSchema from "../general/BreadcrumbSchema";

interface EventDescription {
  subtitle: string;
  text: string;
  imageUrl: string;
}

interface ContactFormsPageProps {
  isOpen?: boolean;
}

const ContactFormsPage: React.FC<ContactFormsPageProps> = ({
  isOpen = false,
}) => {
  const [eventType, setEventType] = useState<"private" | "public">("private");

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["contact-forms-page"],
    queryFn: async () => {
      const [extraCards, faqItems] = await Promise.all([
        fetchExtraCards(),
        fetchFaqItems(),
        loadContactFormsPageData(),
      ]);

      const descriptionPrivateEvents: EventDescription[] = [
        {
          subtitle: PrivateEventsDescription.Subtitle1,
          text: PrivateEventsDescription.Text1,
          imageUrl: "/images/private1.jpg",
        },
        {
          subtitle: PrivateEventsDescription.Subtitle2,
          text: PrivateEventsDescription.Text2,
          imageUrl: "/images/private2.jpg",
        },
        {
          subtitle: PrivateEventsDescription.Subtitle3,
          text: PrivateEventsDescription.Text3,
          imageUrl: "/images/private3.jpg",
        },
      ];

      const descriptionPublicEvents: EventDescription[] = [
        {
          subtitle: PublicEventsDescription.Subtitle1,
          text: PublicEventsDescription.Text1,
          imageUrl: "/images/private3.jpg",
        },
        {
          subtitle: PublicEventsDescription.Subtitle2,
          text: PublicEventsDescription.Text2,
          imageUrl: "/images/private3.jpg",
        },
        {
          subtitle: PublicEventsDescription.Subtitle3,
          text: PublicEventsDescription.Text3,
          imageUrl: "/images/private3.jpg",
        },
      ];

      return { extraCards, faqItems, descriptionPrivateEvents, descriptionPublicEvents };
    },
  });

  const handleEventTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEventType(event.target.value as "private" | "public");
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError || !data) {
    return <ApiErrorScreen onRetry={() => refetch()} />;
  }

  const { extraCards, faqItems, descriptionPrivateEvents, descriptionPublicEvents } =
    data;

  return (
    <StyledBackgroundDiv>
      <SeoHelmet
        title="DJ Mayday | Réservez votre DJ à Liège & Wallonie — Formules & Tarifs"
        description="Découvrez les formules DJ Mayday pour événements privés et publics en Wallonie. Réservez votre DJ pour un mariage, anniversaire ou soirée à Liège et dans toute la région."
        url="https://www.djmayday.com/contact-forms"
      />
      <BreadcrumbSchema
        items={[
          { name: "Accueil", url: "https://www.djmayday.com/" },
          { name: "Mes formules", url: "https://www.djmayday.com/contact-forms" },
        ]}
      />
      <h1 style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>
        Réservez DJ Mayday pour votre événement en Liège et en Wallonie — Formules et tarifs
      </h1>
      <Header />
      <PageIntro
        title={ContactFormsTexts.Title}
        description={ContactFormsTexts.DescriptionText}
        backgroundImage={TopSectionsPictures.FormulasPage}
        modalText={ContactFormsTexts.ModalText}
        buttonText={ContactFormsTexts.ButtonText}
        aboutModal={ContactFormsTexts.AboutModal}
        closeModal={ContactFormsTexts.CloseModal}
      />

      <IntroTypography>
        {" "}
        {FormulasPageGeneralTexts.ExplanationText}{" "}
      </IntroTypography>
      <EventTypeSelector
        eventType={eventType}
        onChange={handleEventTypeChange}
      />
      {eventType === "private" && (
        <>
          <EventTypeDescriptionSection
            descriptions={descriptionPrivateEvents}
          />
          <DoubleEntryTable />
          <RollingGallery
            title={FormulasPageGeneralTexts.ExtraTitle}
            cards={extraCards}
          />
        </>
      )}

      {eventType === "public" && (
        <>
          <EventTypeDescriptionSection descriptions={descriptionPublicEvents} />
          <PublicEventsFormulasTable
            formulasData={PublicEventsFormulasTableTexts}
          />
          <RollingGallery
            title={FormulasPageGeneralTexts.ExtraTitle}
            cards={extraCards}
          />
        </>
      )}

      <FaqSection items={faqItems} />

      <StepFormModal isOpen={isOpen} />
      <StaticFooter />
      <Footer />
    </StyledBackgroundDiv>
  );
};

export default ContactFormsPage;
